import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  XMarkIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface LeaveType {
  id: number
  name: string
  code: string
  maxDaysPerYear: number | null
  carryForwardAllowed: boolean
  maxCarryForwardDays: number
  requiresApproval: boolean
  isPaid: boolean
  description: string | null
  isActive: boolean
  _count: {
    employeeLeaveBalances: number
    leaveRequests: number
  }
}

interface LeaveTypesResponse {
  leaveTypes: LeaveType[]
}

interface LeaveTypeFormData {
  name: string
  code: string
  maxDaysPerYear: string
  carryForwardAllowed: boolean
  maxCarryForwardDays: string
  requiresApproval: boolean
  isPaid: boolean
  description: string
}

interface LeaveTypesModalProps {
  onClose: () => void
  onSuccess: () => void
}

const LeaveTypesModal: React.FC<LeaveTypesModalProps> = ({ onClose}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingLeaveType, setEditingLeaveType] = useState<LeaveType | null>(null)
  const [formData, setFormData] = useState<LeaveTypeFormData>({
    name: '',
    code: '',
    maxDaysPerYear: '',
    carryForwardAllowed: false,
    maxCarryForwardDays: '0',
    requiresApproval: true,
    isPaid: true,
    description: ''
  })

  const queryClient = useQueryClient()

  const { data: leaveTypesData, isLoading } = useQuery<LeaveTypesResponse>({
    queryKey: ['leaveTypes'],
    queryFn: async () => {
      const response = await api.get('/leaves/types')
      return response.data.data
    }
  })

  const createMutation = useMutation({
    mutationFn: async (data: LeaveTypeFormData) => {
      const payload = {
        ...data,
        maxDaysPerYear: data.maxDaysPerYear ? parseInt(data.maxDaysPerYear) : null,
        maxCarryForwardDays: parseInt(data.maxCarryForwardDays)
      }
      const response = await api.post('/leaves/types', payload)
      return response.data
    },
    onSuccess: () => {
      toast.success('Leave type created successfully')
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] })
      resetForm()
      setShowAddForm(false)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create leave type')
    }
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number, data: Partial<LeaveTypeFormData> }) => {
      const payload = {
        ...data,
        maxDaysPerYear: data.maxDaysPerYear ? parseInt(data.maxDaysPerYear) : null,
        maxCarryForwardDays: data.maxCarryForwardDays ? parseInt(data.maxCarryForwardDays) : undefined
      }
      const response = await api.put(`/leaves/types/${id}`, payload)
      return response.data
    },
    onSuccess: () => {
      toast.success('Leave type updated successfully')
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] })
      setEditingLeaveType(null)
      resetForm()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update leave type')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/leaves/types/${id}`)
      return response.data
    },
    onSuccess: () => {
      toast.success('Leave type deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete leave type')
    }
  })

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      maxDaysPerYear: '',
      carryForwardAllowed: false,
      maxCarryForwardDays: '0',
      requiresApproval: true,
      isPaid: true,
      description: ''
    })
  }

  const handleEdit = (leaveType: LeaveType) => {
    setFormData({
      name: leaveType.name,
      code: leaveType.code,
      maxDaysPerYear: leaveType.maxDaysPerYear?.toString() || '',
      carryForwardAllowed: leaveType.carryForwardAllowed,
      maxCarryForwardDays: leaveType.maxCarryForwardDays.toString(),
      requiresApproval: leaveType.requiresApproval,
      isPaid: leaveType.isPaid,
      description: leaveType.description || ''
    })
    setEditingLeaveType(leaveType)
    setShowAddForm(true)
  }

  const handleDelete = async (leaveType: LeaveType) => {
    if (!confirm(`Are you sure you want to delete "${leaveType.name}"? This action cannot be undone.`)) {
      return
    }
    deleteMutation.mutate(leaveType.id)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (editingLeaveType) {
      updateMutation.mutate({ id: editingLeaveType.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? (
          <>
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="w-3 h-3 mr-1" />
            Inactive
          </>
        )}
      </span>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between pb-3 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Manage Leave Types
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-4">
          {!showAddForm ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-gray-900">Leave Types</h4>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                  Add Leave Type
                </button>
              </div>

              {isLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading leave types...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {leaveTypesData?.leaveTypes?.map((leaveType) => (
                    <div key={leaveType.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h5 className="font-medium text-gray-900">{leaveType.name}</h5>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {leaveType.code}
                            </span>
                            {getStatusBadge(leaveType.isActive)}
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>Max Days: {leaveType.maxDaysPerYear || 'Unlimited'}</span>
                            <span>{leaveType.isPaid ? 'Paid' : 'Unpaid'}</span>
                            <span>{leaveType.requiresApproval ? 'Requires Approval' : 'Auto-Approved'}</span>
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            {leaveType._count.employeeLeaveBalances} employees • {leaveType._count.leaveRequests} requests
                          </div>
                          {leaveType.description && (
                            <p className="mt-1 text-sm text-gray-600">{leaveType.description}</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(leaveType)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(leaveType)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-gray-900">
                  {editingLeaveType ? 'Edit Leave Type' : 'Add New Leave Type'}
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    setEditingLeaveType(null)
                    resetForm()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Leave Type Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="e.g., Annual Leave"
                  />
                </div>

                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                    Code
                  </label>
                  <input
                    type="text"
                    name="code"
                    id="code"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="e.g., ANNUAL"
                  />
                </div>

                <div>
                  <label htmlFor="maxDaysPerYear" className="block text-sm font-medium text-gray-700">
                    Max Days Per Year
                  </label>
                  <input
                    type="number"
                    name="maxDaysPerYear"
                    id="maxDaysPerYear"
                    min="0"
                    value={formData.maxDaysPerYear}
                    onChange={(e) => setFormData({ ...formData, maxDaysPerYear: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                <div>
                  <label htmlFor="maxCarryForwardDays" className="block text-sm font-medium text-gray-700">
                    Max Carry Forward Days
                  </label>
                  <input
                    type="number"
                    name="maxCarryForwardDays"
                    id="maxCarryForwardDays"
                    min="0"
                    value={formData.maxCarryForwardDays}
                    onChange={(e) => setFormData({ ...formData, maxCarryForwardDays: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Optional description..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input
                    id="carryForwardAllowed"
                    name="carryForwardAllowed"
                    type="checkbox"
                    checked={formData.carryForwardAllowed}
                    onChange={(e) => setFormData({ ...formData, carryForwardAllowed: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="carryForwardAllowed" className="ml-2 block text-sm text-gray-900">
                    Allow Carry Forward
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="requiresApproval"
                    name="requiresApproval"
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="requiresApproval" className="ml-2 block text-sm text-gray-900">
                    Requires Approval
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="isPaid"
                    name="isPaid"
                    type="checkbox"
                    checked={formData.isPaid}
                    onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPaid" className="ml-2 block text-sm text-gray-900">
                    Paid Leave
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    setEditingLeaveType(null)
                    resetForm()
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingLeaveType
                    ? 'Update Leave Type'
                    : 'Create Leave Type'
                  }
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default LeaveTypesModal